import { useMemo, useState } from "react";
import { Flex } from "@chakra-ui/react";
import getResults from "components/Shared/FilterEditor/getResults";
import { AppliedFilter } from "components/Shared/FilterEditor/utils";
import { useOptionalCombineFilters } from "utils/useCombineFilters";
import { RetailerFilter, RetailerType } from "__generated__/gql/graphql";
import ArchivedStatusButtonGroup, {
  getValueFromFilters,
  IS_ARCHIVED_FILTER_NAME,
  makeFiltersHandleChange,
} from "components/Shared/ArchivedStatusButtonGroup";
import RetailerTypeCheckboxes, {
  getValueFromFilters as getRetailerTypeValueFromFilters,
  makeFiltersHandleChange as makeFiltersHandleRetailerTypeChange,
  RETAILER_TYPE_FILTER_ID,
} from "components/Shared/RetailerDirectory/RetailerTypeCheckboxes";
import FilterBarWithSearch, {
  FreestandingFilterBarContainer,
  FreestandingFilterWell,
} from "components/Shared/FilterEditor/FilterBar/FilterBarWithSearch";
import getFilterDefinitions from "./filterDefinitions";
import RetailersDirectoryTable, { RetailersDirectoryTableProps } from "./Table";

const DEFAULT_FILTERS: AppliedFilter[] = [
  { filter: IS_ARCHIVED_FILTER_NAME, value: false, variant: "equalTo" },
  {
    filter: RETAILER_TYPE_FILTER_ID,
    value: [RetailerType.CHAIN, RetailerType.AFFILIATION],
    variant: "in",
  },
];

export type RetailerDirectoryProps = RetailersDirectoryTableProps & {
  defaultFilters?: AppliedFilter[];
  isAdmin?: boolean;
};

export default function RetailerDirectory({
  defaultFilters = DEFAULT_FILTERS,
  filter: filterProp,
  isAdmin = false,
  ...rest
}: RetailerDirectoryProps) {
  const filterDefinitions = useMemo(
    () => getFilterDefinitions(isAdmin),
    [isAdmin],
  );

  const [filters, setFilters] = useState<AppliedFilter[]>(defaultFilters);

  const filterResults = useMemo(() => {
    if (!filters?.length) return;
    return getResults<RetailerFilter>(filterDefinitions, filters);
  }, [filterDefinitions, filters]);

  const tableFilters = useOptionalCombineFilters(filterProp, filterResults);

  return (
    <>
      <FilterBarWithSearch
        {...{ filterDefinitions, filters }}
        onFiltersChange={setFilters}
        FilterBarContainerComponent={FreestandingFilterBarContainer}
        FiltersContainerComponent={FreestandingFilterWell}
        headerRight={
          <Flex gap={4}>
            <ArchivedStatusButtonGroup
              value={getValueFromFilters(filters)}
              onChange={makeFiltersHandleChange(filters, setFilters)}
              alignSelf="center"
            />

            <RetailerTypeCheckboxes
              size="sm"
              value={getRetailerTypeValueFromFilters(filters)}
              onChange={makeFiltersHandleRetailerTypeChange(
                filters,
                setFilters,
              )}
              listProps={{ lineHeight: "1" }}
            />
          </Flex>
        }
      />
      <RetailersDirectoryTable
        filter={tableFilters}
        isAdmin={isAdmin}
        {...rest}
      />
    </>
  );
}
